import { marketingUrl } from '@ui/lib/urls'

import type { UTMParams } from '@web-app/lib/ironSession'
import type { AttributionData } from '@web-app/types/attribution'

/**
 * Safely extracts attribution data from UTM parameters and session data
 */
export function extractAttributionData(
  utmParams: UTMParams = {},
  sessionId?: string,
  browserFingerprint?: string,
  userId?: string,
  mcid?: string,
): AttributionData {
  // Helper function to safely extract string values
  const safeString = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
    return undefined
  }

  return {
    // UTM parameters - safely extract and validate
    utm_source: safeString(utmParams.utm_source),
    utm_medium: safeString(utmParams.utm_medium),
    utm_campaign: safeString(utmParams.utm_campaign),
    utm_term: safeString(utmParams.utm_term),
    utm_content: safeString(utmParams.utm_content),
    utm_id: safeString(utmParams.utm_id),

    // Affiliate attribution - handle various formats
    aff_id: safeString(utmParams.aff_id),
    aff_offer_id: safeString(utmParams.aff_offer_id),
    aff_sub: safeString(utmParams.aff_sub),
    aff_source: safeString(utmParams.aff_source),

    // Branch specific - handle different parameter variations
    branch_match_id:
      safeString(utmParams.branch_match_id) ||
      safeString(utmParams._branch_match_id),
    click_id:
      safeString(utmParams.click_id) || safeString(utmParams['~click_id']),

    // Marketing campaign ID
    mcid: safeString(mcid),

    // Session identification
    web_session_id: safeString(sessionId),
    web_browser_fingerprint: safeString(browserFingerprint),

    // User identification
    user_id: safeString(userId),

    // Additional context
    attribution_timestamp: new Date().toISOString(),
    attribution_source: 'web_session',
  }
}

/**
 * Checks if the session contains affiliate attribution parameters
 */
export function hasAffiliateAttribution(utmParams: UTMParams = {}): boolean {
  return Boolean(
    utmParams.aff_id ||
      utmParams.aff_offer_id ||
      utmParams.aff_sub ||
      utmParams.aff_source,
  )
}

/**
 * Checks if the session contains any attribution parameters
 */
export function hasAttribution(utmParams: UTMParams = {}): boolean {
  return Boolean(
    utmParams.utm_source ||
      utmParams.utm_medium ||
      utmParams.utm_campaign ||
      utmParams.utm_term ||
      utmParams.utm_content ||
      utmParams.utm_id ||
      utmParams.aff_id ||
      utmParams.aff_offer_id ||
      utmParams.aff_sub ||
      utmParams.aff_source ||
      utmParams.branch_match_id ||
      utmParams._branch_match_id ||
      utmParams.click_id ||
      utmParams['~click_id'],
  )
}

/**
 * Checks if the session contains Branch-specific attribution
 */
export function hasBranchAttribution(utmParams: UTMParams = {}): boolean {
  return Boolean(
    utmParams.branch_match_id ||
      utmParams._branch_match_id ||
      utmParams.click_id ||
      utmParams['~click_id'],
  )
}

/**
 * Safely encodes a parameter value for use in deep links
 */
function safeEncodeParam(value: string): string {
  try {
    // First, ensure the value is properly trimmed and not empty
    const trimmedValue = value.trim()
    if (!trimmedValue) return ''

    // Use encodeURIComponent for robust encoding of special characters
    // This handles spaces, special characters, unicode, etc.
    return encodeURIComponent(trimmedValue)
  } catch (error) {
    console.warn('Error encoding parameter value:', value, error)
    // Fallback: remove potentially problematic characters
    return value.replace(/[^a-zA-Z0-9\-_.~]/g, '')
  }
}

/**
 * Validates that a parameter key is safe for use in URLs
 */
function isValidParamKey(key: string): boolean {
  // Check for valid URL parameter key (alphanumeric, underscore, hyphen)
  return /^[a-zA-Z0-9_-]+$/.test(key)
}

/**
 * Creates a deep link path for the mobile app with attribution context
 */
export function createDeepLinkPath(
  destination = 'complete-registration',
  additionalParams: Record<string, string> = {},
): string {
  try {
    // Validate destination
    const safeDestination = destination.replace(/[^a-zA-Z0-9\-_]/g, '')
    if (!safeDestination) {
      console.warn('Invalid destination provided, using default')
      return 'atmapp://complete-registration'
    }

    const queryParts: string[] = []

    // Add destination-specific parameters with robust encoding
    Object.entries(additionalParams).forEach(([key, value]) => {
      // Validate key and value
      if (!key || !isValidParamKey(key)) {
        console.warn(`Invalid parameter key: ${key}`)
        return
      }

      if (!value || typeof value !== 'string') {
        return
      }

      const trimmedValue = value.trim()
      if (trimmedValue.length === 0) {
        return
      }

      // Check for reasonable value length to prevent issues
      if (trimmedValue.length > 2000) {
        console.error(`Error: Parameter value too long for key ${key}, truncating. Length: ${trimmedValue.length}`)
        const truncatedValue = trimmedValue.substring(0, 2000)
        const encodedValue = safeEncodeParam(truncatedValue)
        queryParts.push(`${key}=${encodedValue}`)
      } else {
        const encodedValue = safeEncodeParam(trimmedValue)
        if (encodedValue) {
          queryParts.push(`${key}=${encodedValue}`)
        }
      }
    })

    const baseDeepLink = `atmapp://${safeDestination}`

    if (queryParts.length === 0) {
      return baseDeepLink
    }

    const queryString = queryParts.join('&')
    return `${baseDeepLink}?${queryString}`
  } catch (error) {
    console.warn('Error creating deep link path:', error)
    // Return safe fallback
    const safeDestination =
      destination.replace(/[^a-zA-Z0-9\-_]/g, '') || 'complete-registration'
    return `atmapp://${safeDestination}`
  }
}

/**
 * Creates Branch link data object with attribution and fallback URLs
 */
export function createBranchLinkData(
  attributionData: AttributionData,
  deepLinkPath?: string,
  customData: Record<string, unknown> = {},
): Record<string, unknown> {
  try {
    const linkData: Record<string, unknown> = {
      // Deep link configuration
      $deeplink_path: deepLinkPath || createDeepLinkPath(),
      $fallback_url: marketingUrl('/download'),
      $ios_url:
        'https://apps.apple.com/us/app/atm-instant-cash-rewards/id1507829573',
      $android_url:
        'https://play.google.com/store/apps/details?id=com.atm.atm.prod',
      $desktop_url: marketingUrl('/download'),

      // Attribution data - safely merge
      ...attributionData,

      // Registration flow context
      registration_source: 'web_complete_registration',
      attribution_preserved: true,
      created_at: new Date().toISOString(),

      // Custom data - safely merge
      ...customData,
    }

    // Remove undefined values and empty strings
    Object.keys(linkData).forEach((key) => {
      const value = linkData[key]
      if (value === undefined || value === null || value === '') {
        delete linkData[key]
      }
    })

    return linkData
  } catch (error) {
    console.warn('Error creating Branch link data:', error)
    // Return minimal safe fallback
    return {
      $deeplink_path: deepLinkPath || createDeepLinkPath(),
      $fallback_url: marketingUrl('/download'),
      $ios_url:
        'https://apps.apple.com/us/app/atm-instant-cash-rewards/id1507829573',
      $android_url:
        'https://play.google.com/store/apps/details?id=com.atm.atm.prod',
      registration_source: 'web_complete_registration',
      created_at: new Date().toISOString(),
    } as Record<string, unknown>
  }
}

/**
 * Validates attribution data structure
 */
export function validateAttributionData(
  data: unknown,
): data is AttributionData {
  return typeof data === 'object' && data !== null && !Array.isArray(data)
}

/**
 * Merges multiple attribution data sources with priority
 * Priority: explicit > session > referring > default
 */
export function mergeAttributionData(
  ...sources: (AttributionData | undefined | null)[]
): AttributionData {
  const validSources = sources.filter((source): source is AttributionData =>
    validateAttributionData(source),
  )

  return validSources.reduce((merged, source) => {
    // Only merge non-empty values
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        ;(merged as Record<string, unknown>)[key] = value
      }
    })
    return merged
  }, {} as AttributionData)
}